import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { toast } from "sonner";
import { motion } from "motion/react";
import { LogOut, PackageOpen } from "lucide-react";
import useAuthStore from "../store/authStore";
import { useMyOrders } from "../features/account/api";
import { orderItemCount, orderRevenue } from "../lib/orderMath";
import { formatPrice } from "../lib/format";
import SCSpinner from "../components/loaders/SCSpinner";
import useSEO from "../hooks/useSEO";

const signInSchema = z.object({
  email: z.string().email("Enter a valid email"),
  password: z.string().min(6, "At least 6 characters"),
});

const registerSchema = signInSchema.extend({
  name: z.string().min(2, "Enter your name"),
});

export default function Account() {
  useSEO({ title: "Account", noindex: true });
  const status = useAuthStore((s) => s.status);
  const user = useAuthStore((s) => s.user);
  const [mode, setMode] = useState("signin");

  if (status === "idle" || status === "loading") {
    return (
      <div className="flex min-h-[50vh] items-center justify-center">
        <SCSpinner size={56} />
      </div>
    );
  }

  if (status === "authenticated" && user) {
    return <AccountDashboard user={user} />;
  }

  return (
    <div className="mx-auto max-w-sm px-4 py-8">
      <h1 className="mb-1 text-center font-serif text-2xl text-ink">
        {mode === "signin" ? "Welcome back" : "Create your account"}
      </h1>
      <p className="mb-6 text-center text-sm text-ink-light">
        {mode === "signin"
          ? "Sign in to track your order requests and wishlist."
          : "Save your details for faster checkout next time."}
      </p>

      <div className="mb-6 flex rounded-full bg-mono/[0.04] p-1 text-sm">
        <button
          onClick={() => setMode("signin")}
          className={`flex-1 rounded-full py-2 transition-colors ${
            mode === "signin" ? "bg-surface text-ink shadow-sm" : "text-ink-light"
          }`}
        >
          Sign In
        </button>
        <button
          onClick={() => setMode("register")}
          className={`flex-1 rounded-full py-2 transition-colors ${
            mode === "register" ? "bg-surface text-ink shadow-sm" : "text-ink-light"
          }`}
        >
          Create Account
        </button>
      </div>

      {mode === "signin" ? <SignInForm /> : <RegisterForm onDone={() => setMode("signin")} />}

      <p className="mt-6 text-center text-xs text-ink-light">
        Prefer not to sign in? You can still browse and check out as a guest.
      </p>
    </div>
  );
}

function SignInForm() {
  const login = useAuthStore((s) => s.login);
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm({ resolver: zodResolver(signInSchema) });

  async function onSubmit({ email, password }) {
    try {
      await login(email, password);
    } catch (err) {
      toast.error(err.response?.data?.error || "Invalid email or password");
    }
  }

  return (
    <motion.form
      onSubmit={handleSubmit(onSubmit)}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="space-y-4"
    >
      <Field label="Email" error={errors.email?.message}>
        <input {...register("email")} type="email" className="checkout-input" placeholder="you@example.com" />
      </Field>
      <Field label="Password" error={errors.password?.message}>
        <input {...register("password")} type="password" className="checkout-input" placeholder="••••••••" />
      </Field>
      <button
        type="submit"
        disabled={isSubmitting}
        className="w-full rounded-2xl bg-[#2b2b2d] py-3.5 text-sm tracking-wide text-white shadow-floating disabled:opacity-60 dark:bg-gold-500 dark:text-ink-dark dark:shadow-gold"
      >
        {isSubmitting ? "Signing in…" : "SIGN IN"}
      </button>
    </motion.form>
  );
}

function RegisterForm({ onDone }) {
  const register_ = useAuthStore((s) => s.register);
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm({ resolver: zodResolver(registerSchema) });

  async function onSubmit({ name, email, password }) {
    try {
      await register_({ name, email, password });
      toast.success(`Welcome, ${name.split(" ")[0]}!`);
      onDone?.();
    } catch (err) {
      toast.error(err.response?.data?.error || "Couldn't create your account — try again.");
    }
  }

  return (
    <motion.form
      onSubmit={handleSubmit(onSubmit)}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="space-y-4"
    >
      <Field label="Full name" error={errors.name?.message}>
        <input {...register("name")} className="checkout-input" placeholder="Jane Doe" />
      </Field>
      <Field label="Email" error={errors.email?.message}>
        <input {...register("email")} type="email" className="checkout-input" placeholder="you@example.com" />
      </Field>
      <Field label="Password" error={errors.password?.message}>
        <input {...register("password")} type="password" className="checkout-input" placeholder="••••••••" />
      </Field>
      <button
        type="submit"
        disabled={isSubmitting}
        className="w-full rounded-2xl bg-[#2b2b2d] py-3.5 text-sm tracking-wide text-white shadow-floating disabled:opacity-60 dark:bg-gold-500 dark:text-ink-dark dark:shadow-gold"
      >
        {isSubmitting ? "Creating account…" : "CREATE ACCOUNT"}
      </button>
    </motion.form>
  );
}

function Field({ label, error, children }) {
  return (
    <label className="block">
      <span className="mb-1.5 block text-xs font-medium uppercase tracking-wide text-ink-light">
        {label}
      </span>
      {children}
      {error && <span className="mt-1 block text-xs text-red-500">{error}</span>}
    </label>
  );
}

function AccountDashboard({ user }) {
  const logout = useAuthStore((s) => s.logout);
  const { data: orders, isLoading } = useMyOrders(true);

  return (
    <div className="px-4 py-6">
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="font-serif text-2xl text-ink">Hi, {user.name.split(" ")[0]}</h1>
          <p className="text-sm text-ink-light">{user.email}</p>
        </div>
        <button
          onClick={() => logout()}
          className="flex items-center gap-1.5 rounded-xl px-3 py-2 text-sm text-ink-light hover:text-ink"
        >
          <LogOut size={15} /> Log out
        </button>
      </div>

      <h2 className="mb-3 font-serif text-lg text-ink">My Orders</h2>
      {isLoading ? (
        <div className="flex justify-center py-10">
          <SCSpinner size={40} />
        </div>
      ) : !orders || orders.length === 0 ? (
        <div className="flex flex-col items-center py-10 text-center">
          <PackageOpen size={32} className="text-mono/15" />
          <p className="mt-3 text-sm text-ink-light">No order requests yet.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {orders.map((o) => (
            <div key={o.uuid} className="rounded-2xl bg-surface p-4 shadow-card">
              <div className="flex items-start justify-between">
                <span className="text-sm text-ink">
                  #{o.id} · {orderItemCount(o.items)} item{orderItemCount(o.items) === 1 ? "" : "s"}
                </span>
                <OrderStatusBadge status={o.status} />
              </div>
              <div className="mt-1 flex items-center justify-between text-xs text-ink-light">
                <span>{new Date(o.createdAt).toLocaleDateString()}</span>
                <span className="text-sm text-ink">{formatPrice(orderRevenue(o.items))}</span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// A lightweight local copy of admin's StatusBadge (pages/admin/Dashboard.jsx)
// rather than importing it — that module is lazy-loaded as part of the
// admin bundle, and pulling from it here would drag the whole admin
// chunk into the storefront's main bundle.
function OrderStatusBadge({ status }) {
  const styles = {
    pending: "bg-amber-50 text-amber-600 dark:bg-amber-500/15 dark:text-amber-300",
    contacted: "bg-blue-50 text-blue-600 dark:bg-blue-500/15 dark:text-blue-300",
    confirmed: "bg-emerald-50 text-emerald-600 dark:bg-emerald-500/15 dark:text-emerald-300",
    fulfilled: "bg-gold-50 text-gold-600 dark:bg-gold-500/15 dark:text-gold-200",
    cancelled: "bg-red-50 text-red-500 dark:bg-red-500/15 dark:text-red-300",
  };
  return (
    <span className={`rounded-full px-2.5 py-1 text-[11px] font-medium capitalize ${styles[status]}`}>
      {status}
    </span>
  );
}
